import React, {useState, useEffect, memo} from 'react'
import PropTypes from 'prop-types'
import {Route, Redirect} from 'react-router-dom'
import {Link} from 'react-router-dom';
import {ServerConfig} from 'config'
import axios from 'axios'
import {useDispatch, useSelector} from 'react-redux'
import {logout} from '../../redux/actions/auth.js'

// @material-ui/core components
import {makeStyles} from '@material-ui/core/styles'
import {Typography, Grid, Hidden, Container, CardHeader} from '@material-ui/core'
import {ListGroup, ListGroupItem} from 'reactstrap';
import {Line, Bar} from '@reactchartjs/react-chart.js';
import Card from "@material-ui/core/Card";
import CardContent from "@material-ui/core/CardContent";

import AccountBalanceWalletIcon from '@material-ui/icons/AccountBalanceWallet';
import ReceiptIcon from '@material-ui/icons/Receipt';
import ImportantDevicesIcon from '@material-ui/icons/ImportantDevices';
import ShoppingBasketIcon from '@material-ui/icons/ShoppingBasket';
import BarChartIcon from '@material-ui/icons/BarChart'
import AssignmentIcon from '@material-ui/icons/Assignment';
import PersonIcon from '@material-ui/icons/Person';

import EcoCountImage from '../../assets/img/ecocount_background.png'
import EcoHubImage from '../../assets/img/ecohub_background.png'

import Page from 'components/Page/Page'
import {getAllowedRoutes} from 'utils';
import _, {get} from 'lodash'

import SortableTbl from "react-sort-search-table";

const useStyles = makeStyles((theme) => ({
  container: {
    paddingLeft: theme.spacing(8),
    paddingRight: theme.spacing(8),
    marginBottom: theme.spacing(8),
    [theme.breakpoints.down('xs')]: {
      paddingLeft: theme.spacing(2),
      paddingRight: theme.spacing(2)
    },
    [theme.breakpoints.up('lg')]: {
      marginBottom: theme.spacing(12)
    }
  },
  listItem: {
    fontSize: '15px',
    listStyle: 'none',
    margin: 0,
    padding: 0
  },
  listGroup: {
    margin: 0,
    padding: 0,
    marginBottom: 20,
  },
}))

const plot_options_receipts = {
  responsive: true,
  maintainAspectRatio: false,
  legend: {
    display: false
  },
  elements: {
    point: {
      radius: 4,
      hitRadius: 10,
    }
  },
  scales: {
    yAxes: [
      {
        type: 'linear',
        display: false,
        position: 'left',
        id: 'y-axis-1',
        gridLines: {zeroLineColor: 'gray', color: 'rgba(128,128,128,0.3)', display: true}
      },
    ],
    xAxes: [{
      type: 'time',
      id: 'x-axis-1',
      ticks: {
        //fontColor: 'rgb(255,255,255)',
        display: false,
      },
      time: {
        unit: "month",
        displayFormats: {
          hour: 'MMM DD hh a',
          day: 'MMM DD',
          month: 'MMM YY',
          quarter: 'MMM YYYY DD',
        },
      },
      offset: true,
      //type: "category",
      gridLines: {zeroLineColor: 'gray', display: false}
    }]
  },
}

const plot_options_materials = {
  responsive: true,
  maintainAspectRatio: false,
  legend: {
    display: true
  },
  scales: {
    yAxes: [
      {
        type: 'linear',
        display: false,
        position: 'left',
        id: 'y-axis-1',
        gridLines: {zeroLineColor: 'gray', color: 'rgba(128,128,128,0.3)', display: true}
      },
      {
        type: 'linear',
        display: false,
        position: 'right',
        id: 'y-axis-2',
        gridLines: {zeroLineColor: 'gray', color: 'rgba(128,128,128,0.3)', display: true}
      },
    ],
    xAxes: [{
      type: 'category',
      id: 'x-axis-1',
      ticks: {
        display: true,
        //fontColor: 'rgb(255,0,255)',
      },
      offset: true,
      //type: "category",
      gridLines: {zeroLineColor: 'gray', display: false}
    }]
  },
};

const plot_options = {
  responsive: true,
  maintainAspectRatio: false,
  legend: {
    display: false
  },
  scales: {
    yAxes: [
      {
        type: 'linear',
        display: false,
        position: 'left',
        id: 'y-axis-1',
        gridLines: {zeroLineColor: 'gray', color: 'rgba(128,128,128,0.3)', display: true}
      },
    ],
    xAxes: [{
      type: 'time',
      id: 'x-axis-1',
      ticks: {
        fontColor: 'rgb(255,255,255)',
      },
      time: {
        unit: "month",
        displayFormats: {
          hour: 'MMM DD hh a',
          day: 'MMM DD',
          month: 'MMM YY',
          quarter: 'MMM YYYY DD',
        },
      },
      offset: true,
      //type: "category",
      gridLines: {zeroLineColor: 'gray', display: false}
    }]
  },
};

const Stats = () => {
  const token = useSelector(state => state.auth.access_token)
  const tenant = useSelector(state => state.auth.tenant)

  const [stats, setStats] = useState([])
  const [balanceGraphData, setBalanceGraphData] = useState({datasets: []})
  const [receiptsGraphData, setReceiptsGraphData] = useState({datasets: []})
  const [materialsGraphData, setMaterialsGraphData] = useState({datasets: []})
  const [materialsWeightGraphData, setMaterialsWeightGraphData] = useState({datasets: []})

  const classes = useStyles()

  useEffect(() => {
    const fetchStats = async () => {
      const config = {headers: {'Content-Type': 'application/json'}}

      if (token) {
        config.headers.Authorization = `Bearer ${token}`
      }

      const results = await axios.get(`${ServerConfig.SERVER_URL}/tenant/stats`, config)
      setStats(results.data);

      /* Materials graph by weight */
      var materials_graph_weight_labels = Object.keys(results.data["Materials"]["weights"]);
      var materials_graph_weight_data = Object.values(results.data["Materials"]["weights"]);

      let material_weight_background_colors = [];
      let this_color;

      for (let idx in materials_graph_weight_labels) {
        if (materials_graph_weight_labels[idx] == "Aluminum") {
          this_color = 'rgb(0, 142, 170)';  // RT-blue
        } else if (materials_graph_weight_labels[idx] == "Bi-Metal") {
          this_color = 'rgb(124, 135, 143)';  // RT-gray
        } else if (materials_graph_weight_labels[idx] == "Plastic") {
          this_color = 'rgb(151, 215, 0)';  // RT-green
        } else if (materials_graph_weight_labels[idx] == "WDS-BBP") {
          this_color = 'rgb(255, 200, 69)';
        } else {
          this_color = 'rgba(5, 195, 221, 0.2)'; // blue
        }
        material_weight_background_colors.push(this_color);
      }
      // this dataset is added to the materials-count-datasets below to display in ONE barchart

      /* Materials graph by counts */
      var materials_graph_labels = Object.keys(results.data["Materials"]["counts"]);
      var materials_graph_data = Object.values(results.data["Materials"]["counts"]);

      let material_background_colors = [];

      for (let idx in materials_graph_labels) {
        if (materials_graph_labels[idx] == "Aluminum") {
          this_color = 'rgb(0, 142, 170)';  // RT-blue
        } else if (materials_graph_labels[idx] == "Bi-Metal") {
          this_color = 'rgb(124, 135, 143)';  // RT-gray
        } else if (materials_graph_labels[idx] == "Plastic") {
          this_color = 'rgb(151, 215, 0)';  // RT-green
        } else if (materials_graph_labels[idx] == "WDS-BBP") {
          this_color = 'rgb(255, 200, 69)';
        } else {
          this_color = 'rgba(5, 195, 221, 0.2)'; // blue
        }
        material_background_colors.push(this_color);
      }

      var materials_graph_datasets = [
        {  // BY COUNT
          type: 'bar',
          label: 'Count/Material',
          data: materials_graph_data,
          fill: true,

          backgroundColor: material_background_colors,
          borderColor: 'rgb(5, 195, 221)',

          yAxisID: 'y-axis-1',
          xAxisID: 'x-axis-1',
        },
        {  // BY WEIGHT
          type: 'bar',
          label: 'lbs/Material',
          data: materials_graph_weight_data,
          fill: true,

          backgroundColor: 'rgb(255,255,255)',
          borderColor: material_weight_background_colors,
          borderWidth: 2,

          yAxisID: 'y-axis-2',
          xAxisID: 'x-axis-1',
        },
      ];

      setMaterialsGraphData({labels: materials_graph_labels, datasets: materials_graph_datasets});

      /* Number of receipts graph */
      var receipt_graph_labels = Object.keys(results.data["NumTasksHistory"]);
      var receipt_graph_data = Object.values(results.data["NumTasksHistory"]);

      var receipt_graph_datasets = [
        {
          type: 'line',
          label: '#Receipts',
          data: receipt_graph_data,
          fill: true,

          backgroundColor: 'rgba(5, 195, 221, 0.2)',
          borderColor: 'rgb(5, 195, 221)',
          //pointBorderColor: 'rgb(5, 195, 221)',
          //pointBackgroundColor: 'rgb(5, 195, 221)',

          yAxisID: 'y-axis-1',
          xAxisID: 'x-axis-1',
        },
      ];

      setReceiptsGraphData({labels: receipt_graph_labels, datasets: receipt_graph_datasets});

      /* Balance graph */
      var total_graph_labels = Object.keys(results.data["BalanceHistory"]);
      var total_graph_data = Object.values(results.data["BalanceHistory"]);

      let background_colors = [];
      for (let idx in total_graph_data) {
        if (total_graph_data[idx] < 0) {
          background_colors.push('rgb(190, 77, 0)'); // orange
        } else {
          //background_colors.push('rgb(151, 215, 0)'); //
          background_colors.push('rgb(5, 195, 221)'); // light blue
        }
      }

      var total_graph_datasets = [
        {
          type: 'bar',
          label: 'Balance',
          data: total_graph_data,
          fill: false,

          borderColor: background_colors,
          backgroundColor: background_colors,

          barThickness: 8,
          maxBarThickness: 8,

          borderWidth: 2,
          borderRadius: Number.MAX_VALUE,
          borderSkipped: false,

          yAxisID: 'y-axis-1',
          xAxisID: 'x-axis-1',
        },

        {  // add additional points
          type: 'line',
          label: 'line',
          data: total_graph_data,
          fill: false,

          backgroundColor: 'rgba(0,0,0,1)',
          borderColor: 'rgba(0,0,0,0)',
          pointBorderColor: background_colors,
          pointBackgroundColor: background_colors,

          yAxisID: 'y-axis-1',
          xAxisID: 'x-axis-1',
        },
      ]

      setBalanceGraphData({labels: total_graph_labels, datasets: total_graph_datasets});
    }

    fetchStats()
  }, [])

  if (!token) {
    return <Redirect to="/login"/>
  }

  return (
    <>
      {_.map(stats, (data, key) => {
          if (key == "BalanceHistory") {
              return (
                  <Grid item xs={12} sm={12} md={3} xl={3}>
                      <Card key={key} variant="outlined"
                            style={{'color': 'white', 'backgroundColor': 'rgb(0,79,113)'}}>
                          <CardContent>
                              <div style={{'float': 'right', 'fontSize': 'small'}}>Past 6 months</div>
                              <div style={{
                                  'borderRadius': '50%',
                                  'color': 'rgb(0,79,113)',
                                  'borderColor': 'white',
                                  'backgroundColor': 'white',
                                  'height': '70px',
                                  'width': '70px',
                                  'textAlign': 'center',
                                  'paddingTop': '18px'
                              }}>
                                  <AccountBalanceWalletIcon fontSize="large"/>
                              </div>
                              <br/>
                              Balance:<br style={{'height': '10px'}}/>
                              <div style={{
                                  'fontSize': '32px',
                                  'fontWeight': 'bold',
                                  'marginBottom': '0px'
                              }}>${data[Object.keys(data)[Object.keys(data).length - 1]]}</div>
                              <br/>
                              <div style={{'height': '150px'}}>
                                  <Bar data={balanceGraphData} options={plot_options} resizeDelay={0}/>
                              </div>
                          </CardContent>
                      </Card>
                  </Grid>
              );
          } else if (key == "NumTasksHistory") {
              return (
                  <Grid item xs={12} sm={6} md={4} xl={3}>
                      <Card key={key} variant="outlined" style={{}}>
                          <CardContent>
                              <div style={{'float': 'right', 'fontSize': 'small'}}>Past 6 months</div>
                              <div style={{
                                  'borderRadius': '50%',
                                  'color': 'rgba(5, 195, 221, 1)',
                                  'borderColor': 'white',
                                  'backgroundColor': 'rgba(5, 195, 221, 0.1)',
                                  'height': '70px',
                                  'width': '70px',
                                  'textAlign': 'center',
                                  'paddingTop': '18px'
                              }}>
                                  <ReceiptIcon fontSize="large"/>
                              </div>
                              <br/>
                              #Receipts (this month):<br/>
                              <div style={{
                                  'fontSize': '32px',
                                  'fontWeight': 'bold',
                                  'marginBottom': '0 px'
                              }}>{data[Object.keys(data)[Object.keys(data).length - 1]]}</div>
                              <br/>
                              <div style={{'height': '150px'}}>
                                  <Bar data={receiptsGraphData} options={plot_options_receipts} resizeDelay={0}/>
                              </div>
                          </CardContent>
                      </Card>
                  </Grid>
              );
          }else if (key == "Ecocontrols"){
              if(Object.keys(data).length) {
                  return (
                      <Grid item xs={12} sm={12} md={12} xl={6}>
                          <Card key={key} variant="outlined">
                              <CardContent style={{
                                  'backgroundImage': 'url(' + EcoHubImage + ')',
                                  'backgroundRepeat': 'no-repeat',
                                  'backgroundPosition': '85% 50%',
                                  'backgroundSize': 'contain'
                              }}>
                                  <div style={{'float': 'right', 'fontSize': 'small', 'maxHeight': '200px'}}></div>
                                  <div style={{
                                      'borderRadius': '50%',
                                      'color': 'rgba(5, 195, 221, 1)',
                                      'borderColor': 'white',
                                      'backgroundColor': 'rgba(5, 195, 221, 0.1)',
                                      'height': '70px',
                                      'width': '70px',
                                      'textAlign': 'center',
                                      'paddingTop': '18px'
                                  }}>
                                      <ImportantDevicesIcon fontSize="large"/>
                                  </div>
                                  <br/>
                                  Ecocontrols
                                  <div style={{
                                      'fontSize': '32px',
                                      'fontWeight': 'bold',
                                      'marginBottom': '0px'
                                  }}>{Object.keys(data).length}</div><br/>

                                  <div style={{'height': '150px', 'overflowY': 'clip'}}>
                                      <Grid container xs={12}>
                                          <Grid xs={1} sm={1}></Grid>
                                          <Grid xs={6} sm={3}></Grid>
                                          <Grid xs={5} sm={3}>Location</Grid>
                                          <Hidden xsDown>
                                              <Grid sm={1}>Batt.</Grid>
                                              <Grid sm={1}>Temp.</Grid>
                                              <Grid sm={1}>Fan</Grid>
                                          </Hidden>
                                      </Grid>{/* container */}
                                      {_.map(data, (data_ecocontrol, key_ecocontrol) => {
                                          return (
                                              <>
                                                  <Grid container xs={12}>
                                                      <Grid xs={1} style={{
                                                          'alignContent': 'center',
                                                          'textAlign': 'right',
                                                          'padding': '0.5rem'
                                                      }}>
                                                          {true &&
                                                              <div style={{
                                                                  'borderRadius': '50%',
                                                                  'backgroundColor': 'rgb(5, 195, 221)',
                                                                  'width': '10px',
                                                                  'height': '10px'
                                                              }}></div>
                                                          }
                                                      </Grid>

                                                      <Grid xs={4} sm={3} style={{
                                                          'overflow': 'hidden',
                                                          'whiteSpace': 'nowrap',
                                                          'textOverflow': 'ellipsis'
                                                      }}>
                                                          {key_ecocontrol}
                                                      </Grid>

                                                      <Grid xs={5} sm={3}>
                                                          {data_ecocontrol.location}
                                                      </Grid>

                                                      <Hidden xsDown>
                                                          <Grid sm={1}>
                                                              {data_ecocontrol.battery_percent}
                                                          </Grid>

                                                          <Grid sm={1}>
                                                              {data_ecocontrol.temperature}
                                                          </Grid>
                                                          <Grid sm={1}>
                                                              {data_ecocontrol.fan_status}
                                                          </Grid>
                                                      </Hidden>
                                                  </Grid>
                                              </>
                                          );
                                      })
                                      }
                                  </div>
                              </CardContent>
                          </Card>
                      </Grid>
                  )
              }// if data.length > 0
          }else if (key == "Devices") {
            return (
            <Grid item xs={12} sm={12} md={9} xl={6}>
              <Card key={key} variant="outlined">
                <CardContent style={{
                  'backgroundImage': 'url(' + EcoCountImage + ')',
                  'backgroundRepeat': 'no-repeat',
                  'backgroundPosition': '85% 50%',
                  'backgroundSize': 'contain'
                }}>
                  <div style={{'float': 'right', 'fontSize': 'small', 'maxHeight': '200px'}}>
                    {/*<img src={EcoCountImage} style={{'height': '100%', 'maxHeight': '200px' }} />*/}
                  </div>
                  <div style={{
                    'borderRadius': '50%',
                    'color': 'rgba(5, 195, 221, 1)',
                    'borderColor': 'white',
                    'backgroundColor': 'rgba(5, 195, 221, 0.1)',
                    'height': '70px',
                    'width': '70px',
                    'textAlign': 'center',
                    'paddingTop': '18px'
                  }}>
                    <ImportantDevicesIcon fontSize="large"/>
                  </div>
                  <br/>
                  Devices
                  <div style={{
                    'fontSize': '32px',
                    'fontWeight': 'bold',
                    'marginBottom': '0px'
                  }}>{Object.keys(data).length}</div><br/>

                  <div style={{'height': '150px', 'overflowY': 'scroll'}}>
                    <Grid container xs={12}>
                      <Grid xs={1} sm={1}></Grid>
                      <Grid xs={5} sm={3}></Grid>
                      <Grid xs={6} sm={3}>Location</Grid>
                      <Hidden xsDown>
                        <Grid sm={2}>Bought #</Grid>
                        <Grid sm={2}>Bought Lbs</Grid>
                      </Hidden>
                    </Grid>{/* container */}
                    {_.map(data, (data_machine, key_machine) => {
                      return (
                        <>
                          <Grid container xs={12}>
                            <Grid xs={1} style={{
                              'alignContent': 'center',
                              'textAlign': 'right',
                              'padding': '0.5rem'
                            }}>
                              {(data_machine.devicetype == "ecocount" && data_machine.status) &&
                                <div style={{
                                  'borderRadius': '50%',
                                  'backgroundColor': 'rgb(151, 215, 0)',
                                  'width': '10px',
                                  'height': '10px'
                                }}></div>
                              }
                              {(data_machine.devicetype == "ecocount" && !data_machine.status) &&
                                <div style={{
                                  'borderRadius': '50%',
                                  'backgroundColor': 'rgb(190, 77, 0)',
                                  'width': '10px',
                                  'height': '10px'
                                }}></div>
                              }
                              {data_machine.devicetype == "tablet" &&
                                <div style={{
                                  'borderRadius': '50%',
                                  'backgroundColor': 'rgb(5, 195, 221)',
                                  'width': '10px',
                                  'height': '10px'
                                }}></div>
                              }
                            </Grid>

                            <Grid xs={5} sm={3} style={{
                              'overflow': 'hidden',
                              'whiteSpace': 'nowrap',
                              'textOverflow': 'ellipsis'
                            }}>
                              {key_machine}
                            </Grid>

                            <Grid xs={6} sm={3}>
                              {data_machine.location}
                            </Grid>

                            <Hidden xsDown>
                              <Grid sm={2}>
                                {data_machine.num_containers_total}
                              </Grid>

                              <Grid sm={2}>
                                {data_machine.weight_containers_total}
                              </Grid>
                            </Hidden>
                          </Grid>
                        </>
                      );
                    })
                    }
                  </div>
                </CardContent>
              </Card>
            </Grid>
          )
        } else if (key == "Materials") {
          return (
            <Grid item xs={12} sm={6} md={4} xl={3}>
              <Card key={key} variant="outlined">
                <CardContent>
                  <div style={{'float': 'right', 'fontSize': 'small'}}>Total</div>
                  <div style={{
                    'borderRadius': '50%',
                    'color': 'rgb(151, 215, 0)',
                    'borderColor': 'white',
                    'backgroundColor': 'rgba(151, 215, 0, 0.1)',
                    'height': '70px',
                    'width': '70px',
                    'textAlign': 'center',
                    'paddingTop': '18px'
                  }}>
                    <ShoppingBasketIcon fontSize="large"/>
                  </div>
                  <br/>
                  Material bought
                  <br/>
                  <div style={{
                    'fontSize': '32px',
                    'fontWeight': 'bold',
                    'marginBottom': '0px'
                  }}>&nbsp;</div>
                  <br/>
                  <div style={{'height': '150px'}}>
                    <Bar data={materialsGraphData} options={plot_options_materials}
                         resizeDelay={0}/>
                  </div>
                </CardContent>
              </Card>
            </Grid>
          )
        } else if (key == "Signed up Customers") {
          return <>
            <Grid item xs={12} sm={6} md={4} xl={3}>
              <Card key={key} variant="outlined">
                <CardContent>
                  <div style={{'float': 'right', 'fontSize': 'small'}}>Total</div>
                  <div style={{
                    'borderRadius': '50%',
                    'color': 'rgb(255, 200, 69)',
                    'borderColor': 'white',
                    'backgroundColor': 'rgba(255, 200, 69, 0.1)',
                    'height': '70px',
                    'width': '70px',
                    'textAlign': 'center',
                    'paddingTop': '18px'
                  }}>
                    <PersonIcon fontSize="large"/>
                  </div>
                  <br/>
                  {key}:<br/>
                  <div style={{
                    'fontSize': '32px',
                    'fontWeight': 'bold',
                    'marginBottom': '0px'
                  }}>{data}</div>
                  <br/>
                  {tenant.shortcode != null &&
                    <div style={{'height': '150px'}}>
                      <>
                        <div style={{'width': '100%', 'textAlign': 'center'}}>
                          Your Signup link:<br/>
                          <br/>

                          <a style={{'cursor': 'pointer'}}
                             alt="Copy to clipboard"
                             title="Copy to clipboard"
                             onClick={() => {
                               navigator.clipboard.writeText("https://recycletek.co/signup#" + tenant.shortcode)
                             }}>
                            <AssignmentIcon/> &nbsp; {"recycletek.co/signup#" + tenant.shortcode}
                          </a>
                        </div>
                      </>
                    </div>
                  }
                </CardContent>
              </Card>
            </Grid>
          </>
        } else {
          return (
            <Grid item xs={12} sm={6} md={3}>
              <Card key={key} variant="outlined">
                <CardContent>
                  <div style={{'float': 'right', 'fontSize': 'small'}}>Total</div>
                  <div style={{
                    'borderRadius': '50%',
                    'color': 'rgba(5, 195, 221, 1)',
                    'borderColor': 'white',
                    'backgroundColor': 'rgba(5, 195, 221, 0.1)',
                    'height': '70px',
                    'width': '70px',
                    'textAlign': 'center',
                    'paddingTop': '18px'
                  }}>
                    <BarChartIcon fontSize="large"/>
                  </div>
                  <br/>
                  {key}:<br/>
                  <div style={{
                    'fontSize': '32px',
                    'fontWeight': 'bold',
                    'marginBottom': '0px'
                  }}>{data}</div>
                  <br/>
                </CardContent>
              </Card>
            </Grid>
          );
        }
      })
      }
    </>
  )
}

export default Stats
